import { Formik, useFormikContext } from 'formik';
import Honeybadger from 'honeybadger-js';
import React, { useEffect, useReducer } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ObjectSchema } from 'yup';

import { Wws_PageItem } from '../../generated/graphql';
import CVAlert from '../CVAlert';
import Confirmable from './Confirmable';
import LayoutHeader from './LayoutHeader';
import { dashboardRoute, previewRoute } from '../../helpers/routes';

interface Props {
  close: (pageItem?: Wws_PageItem) => void;
  headerText: string;
  validationSchema: ObjectSchema;
  initialValues: any;
  hiddenFields?: string[];
  onSubmit: (values: any) => Promise<void>;
  Component: React.FC<any>;
  loading?: boolean;
  updating: boolean;
  item?: any;
  pageId: number;
  isNew: boolean;
  onDelete: () => Promise<void>;
}

interface State {
  canceling: boolean;
  formChanged: boolean;
  erroring: boolean;
}

interface Action {
  type: string;
  payload?: boolean;
}

const reducer = (prevState: State, action: Action) => {
  switch (action.type) {
    case 'FORM_CHANGED': {
      return {
        ...prevState,
        formChanged: !!action.payload,
      };
    }
    case 'CONFIRM_CANCEL': {
      return {
        ...prevState,
        canceling: true,
      };
    }
    case 'UNCONFIRM_CANCEL': {
      return {
        ...prevState,
        canceling: false,
      };
    }
    case 'ERROR': {
      return {
        ...prevState,
        erroring: true,
      };
    }
    case 'CLEAR_ERROR': {
      return {
        ...prevState,
        erroring: false,
      };
    }
    default: {
      return prevState;
    }
  }
};

const initialState: State = {
  canceling: false,
  formChanged: false,
  erroring: false,
};

interface EffectProps {
  onDirtyChange: (dirty: boolean) => void;
  formChanged: boolean;
}

const FormikEffect: React.FC<EffectProps> = ({ onDirtyChange, formChanged }) => {
  const { dirty } = useFormikContext();

  useEffect(() => {
    if (dirty !== formChanged) {
      onDirtyChange(dirty);
    }
  }, [dirty, onDirtyChange, formChanged]);

  return null;
};

const Layout: React.FC<Props> = ({
  headerText,
  validationSchema,
  initialValues,
  onSubmit,
  Component,
  item,
  isNew,
  onDelete,
  ...componentProps
}) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const [{ formChanged, canceling, erroring }, dispatch] = useReducer(reducer, initialState);
  const trySubmit = async (values: any) => {
    try {
      await onSubmit(values);
    } catch (error) {
      Honeybadger.notify(error, {
        message: 'Error saving page item',
        context: {
          error,
        },
      });
      dispatch({
        type: 'ERROR',
      });
    }
  };
  const preview = pathname.includes('preview');
  const onCloseRoute = preview ? previewRoute : dashboardRoute;
  const { close, discardOrDelete } = isNew
    ? {
        close: history.goBack,
        discardOrDelete: () => history.push(onCloseRoute),
      }
    : {
        close: () => history.push(onCloseRoute),
        discardOrDelete: async () => {
          try {
            await onDelete();
            history.replace(onCloseRoute);
          } catch (error) {
            dispatch({
              type: 'ERROR',
            });
            Honeybadger.notify(error, {
              message: 'Error deleting page item',
              context: {
                error,
              },
            });
          }
        },
      };
  const leftButtonOnClick = () => (formChanged ? dispatch({ type: 'CONFIRM_CANCEL' }) : close());
  const triggerDirtyChange = (dirty: boolean) => dispatch({ type: 'FORM_CHANGED', payload: dirty });

  return (
    <>
      <LayoutHeader headerText={headerText} isNew={isNew} onClose={leftButtonOnClick} as="h1" preview={preview} />
      <CVAlert
        erroring={erroring}
        onTimeout={() =>
          dispatch({
            type: 'CLEAR_ERROR',
          })
        }
      />
      <Formik enableReinitialize validationSchema={validationSchema} initialValues={initialValues} onSubmit={trySubmit}>
        {({ submitForm, setFieldValue, ...rest }): JSX.Element => (
          <>
            <FormikEffect onDirtyChange={triggerDirtyChange} formChanged={formChanged} />
            <Confirmable
              canceling={canceling}
              onContinue={close}
              onCancel={() => dispatch({ type: 'UNCONFIRM_CANCEL' })}
            >
              <Component
                submitForm={submitForm}
                setFieldValue={setFieldValue}
                item={item}
                isNew={isNew}
                discardOrDelete={discardOrDelete}
                {...rest}
                {...componentProps}
              />
            </Confirmable>
          </>
        )}
      </Formik>
    </>
  );
};

export default Layout;
